import React, { useEffect } from "react";
import { initializeMetaPixelOnAppLoad } from "./utils/metaPageTracking";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { CheckoutProvider } from "./checkout";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/ProfessionalToasts.css';
import ErrorBoundary from "./components/ErrorBoundary";
import CriticalErrorHandler from "./utils/criticalErrorHandler";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import QuickServices from "./components/QuickServices";
import ReportServices from "./components/ReportServices";
import NumerologyServices from "./components/NumerologyServices";
import AppDownload from "./components/AppDownload";
import Banner from "./components/Banner";
import Footer from "./components/Footer";
import MobileBottomNav from "./components/MobileBottomNav";
import Download from "./pages/Download";

import Login from "./pages/consultant/Login";
import WalletRecharge from "./pages/consultant/WalletRecharge";
import PrivateUserRoute from "./components/PrivateUserRoute";
import ProfileList from "./pages/consultant/ProfileList";
import WalletPassbook from "./pages/consultant/WalletPassbook";
import OrderHistory from "./pages/consultant/OrderHistory";
import OrderDetails from "./pages/consultant/OrderDetails";
import UserProfile from "./pages/consultant/UserProfile";
import LandingPageEntry from "./pages/LandingPageEntry";
import NumerologyLandingPage from "./pages/NumerologyLandingPage";
import OrderSuccess from "./pages/OrderSuccess";
import CommonPaymentPage from "./pages/CommonPaymentPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutRedirectCallback from "./pages/CheckoutRedirectCallback";
import AboutUs from "./pages/AboutUs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import RefundPolicy from "./pages/RefundPolicy";
import ShippingDeliveryPolicy from "./pages/ShippingDeliveryPolicy";
import PaymentCallback from "./pages/PaymentCallback";
import LocationSearchDemo from "./pages/LocationSearchDemo";

function App() {
  useEffect(() => {
    // Initialize critical error handler (only logs critical errors)
    CriticalErrorHandler.init();
    
    // Initialize Meta Pixel based on database configuration
    initializeMetaPixelOnAppLoad();
    
    const script = document.createElement("script");
    script.src = "/resources/scripts/script.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const location = useLocation();
  const isConsultantRoute = location.pathname.startsWith("/consultant");
  // Same focused-flow exclusion as /landing and /checkout — numerology's landing and
  // payment pages have their own logo/contact sections (see NumerologyLandingPage.jsx),
  // no global site nav/footer around them. Must be "/numerology/" (with the trailing slash)
  // so it only matches the dynamic /numerology/:sku page, not /numerology-services (the home
  // page's "View All" listing, which should keep the normal header/footer like
  // /prashnakundali and /remedy-services do).
  const isLandingpage = location.pathname.startsWith("/landing") || location.pathname.startsWith("/numerology/");
  const isCheckoutRoute = location.pathname.startsWith("/checkout") || location.pathname.startsWith("/order-payment");

  return (
    <ErrorBoundary>
      <CheckoutProvider>
      {!isLandingpage && !isCheckoutRoute && <Header />}
      <Routes>
        <Route
          path="/"
          element={
            <>
              <HeroSection />
              <ReportServices variant="carousel" />
              <QuickServices variant="carousel" />
              <NumerologyServices variant="carousel" />
              <AppDownload />
              <Banner />
            </>
          }
        />
        <Route path="/location-search" element={<LocationSearchDemo />} />
        <Route path="/prashnakundali" element={<ReportServices />} />
        <Route path="/remedy-services" element={<QuickServices />} />
        <Route path="/numerology-services" element={<NumerologyServices />} />
        <Route path="/download/:orderId" element={<Download />} />
        
        <Route path="/aboutus" element={<AboutUs />} />
        <Route path="/payment-callback" element={<PaymentCallback />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-condition" element={<TermsAndConditions />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/shipping-delivery-policy" element={<ShippingDeliveryPolicy />} />
        
        <Route path="/consultant/" element={<Login />} />
        <Route
          path="/consultant/wallet-recharge/"
          element={
            <PrivateUserRoute>
              <WalletRecharge />
            </PrivateUserRoute>
          }
        />
        <Route
          path="/consultant/wallet-passbook/"
          element={
            <PrivateUserRoute>
              <WalletPassbook />
            </PrivateUserRoute>
          }
        />
        <Route
          path="/consultant/order-history/"
          element={
            <PrivateUserRoute>
              <OrderHistory />
            </PrivateUserRoute>
          }
        />
        <Route
          path="/consultant/order-details/:orderId"
          element={
            <PrivateUserRoute>
              <OrderDetails />
            </PrivateUserRoute>
          }
        />
        <Route
          path="/consultant/profile-list/"
          element={
            <PrivateUserRoute>
              <ProfileList />
            </PrivateUserRoute>
          }
        />
        <Route
          path="/consultant/profile/"
          element={
            <PrivateUserRoute>
              <UserProfile />
            </PrivateUserRoute>
          }
        />
        <Route path="/landing/:id" element={<LandingPageEntry />} />
        <Route path="/numerology/:sku" element={<NumerologyLandingPage />} />
        <Route path="/order-payment" element={<CommonPaymentPage />} />
        <Route path="/order-success/:orderId" element={<OrderSuccess />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout-callback" element={<CheckoutRedirectCallback />} />
        {/* Any unknown URL goes to the home page (replace: Back doesn't bounce into the bad URL) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isLandingpage && !isCheckoutRoute && <Footer />}
      {!isLandingpage && !isCheckoutRoute && <MobileBottomNav />}
      
      {/* Professional Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{
          backgroundColor: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      />
      </CheckoutProvider>
    </ErrorBoundary>
  );
}

export default App;
